import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, ListGroup, Button } from 'react-bootstrap';
import axios from './axiosConfig'; // 导入自定义 axios 实例
import './FolderDashboards.css'; // 自定义样式文件

interface Dashboard {
    id: number;
    name: string;
}

const FolderDashboards: React.FC = () => {
    const { name } = useParams<{ name: string }>(); // 从 URL 中获取 name 参数
    const navigate = useNavigate(); // Use useNavigate hook
    const [dashboards, setDashboards] = useState<Dashboard[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState<number>(1);
    const dashboardsPerPage = 5; // 每页显示的仪表板数量

    useEffect(() => {
        const fetchDashboards = async () => {
            try {
                const response = await axios.get(`/api/folders/${name}/dashboards`, {
                    headers: {
                        'Cache-Control': 'no-cache'
                    }
                });
                const data = response.data;
                console.log('Fetched dashboards:', data);

                if (data && Array.isArray(data.dashboards)) {
                    setDashboards(data.dashboards);
                } else {
                    console.error('Expected an array but got:', data);
                    setDashboards([]);
                }
            } catch (error) {
                console.error('Error fetching dashboards', error);
                setError('Failed to load dashboards. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboards();
    }, [name]);

    // Get current dashboards for the page
    const indexOfLastDashboard = currentPage * dashboardsPerPage;
    const indexOfFirstDashboard = indexOfLastDashboard - dashboardsPerPage;
    const currentDashboards = dashboards.slice(indexOfFirstDashboard, indexOfLastDashboard);

    // Change page
    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const handleDashboardClick = async (dashboardName: string) => {
        try {
            const response = await axios.get(`/api/dashboard/${dashboardName}`);
            const dashboardData = response.data;

            // 假设你想在成功获取数据后跳转到另一个页面，或在当前页面显示数据
            console.log('Fetched dashboard:', dashboardData);
            // 例如，你可以使用React Router的`useNavigate`进行导航
            navigate(`/dashboard/details/${dashboardName}`, { state: { dashboardData } });

            // 或者在当前组件中设置状态并显示数据
        } catch (error) {
            console.error('Error fetching dashboard', error);
            setError('Failed to load dashboard. Please try again later.');
        }
    };

    if (loading) {
        return <p>Loading dashboards...</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <Container fluid className="full-height">
            <Row className="full-height">
                <Col md={12} className="dashboard-list">
                    <h2>Dashboards in Folder {name}</h2>
                    {currentDashboards.length === 0 ? (
                        <p>No dashboards found in this folder.</p>
                    ) : (
                        <>
                            <ListGroup>
                                {currentDashboards.map(dashboard => (
                                    <ListGroup.Item key={dashboard.id}>
                                        <Button
                                            variant="link"
                                            onClick={() => handleDashboardClick(dashboard.name)}
                                        >
                                            {dashboard.name}
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            <div className="pagination">
                                <Button
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <span style={{ margin: '0 10px' }}>Page {currentPage}</span>
                                <Button
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={indexOfLastDashboard >= dashboards.length}
                                >
                                    Next
                                </Button>
                            </div>
                        </>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default FolderDashboards;
